import { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBoxRounded } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { Modal, Table } from '../../../components';
import {
  getDynamicDocumentationFileTemplatesHistory,
  updateDynamicDocumentationFileTemplate
} from '../../../fetch';
import { IDynamicFileMangementTemplate } from '../../../models';
import { useSnackbar } from 'notistack';
import { formatDate } from '../../../helpers';
import { useQueryClient } from 'react-query';

export const DynamicFileManagementHistoryModal = ({
  handleClose,
  documentName,
  fetchRecentTemplates
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: templateHistory,
    isLoading: isLoadingContractHistory,
    refetch: fetchHistory
  } = useQuery(
    ['getSalesToolsContractHistory-contract-management', documentName],
    () => getDynamicDocumentationFileTemplatesHistory(documentName),
    {
      enabled: !!documentName
    }
  );

  return (
    <Modal open title="File History" maxWidth="md" onClose={() => handleClose()}>
      <Box>
        <Table
          columns={[
            {
              Header: 'Active',
              accessor: 'isActive',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: IDynamicFileMangementTemplate } };
              }) => {
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={
                        original.isDeletedInPandaDoc
                          ? 'Template is deleted in Pandadoc'
                          : original.isActive
                          ? 'Set as "Inactive"'
                          : 'Set as "Active"'
                      }
                    >
                      <span>
                        <IconButton
                          sx={{ padding: 0 }}
                          size="small"
                          disabled={original.isDeletedInPandaDoc}
                          onClick={async () => {
                            try {
                              setIsUpdating(true);
                              await updateDynamicDocumentationFileTemplate(
                                original.pandaDocTemplateId,
                                { isActive: !original.isActive }
                              );
                              fetchHistory();
                              fetchRecentTemplates();
                              // Avoids prop drilling and refetchs this query for the active status on accordion
                              queryClient.invalidateQueries([
                                'getDynamicDocumentationFileTemplates'
                              ]);
                              enqueueSnackbar(`File updated!`, {
                                variant: 'success'
                              });
                            } catch (error: any) {
                              enqueueSnackbar(error?.Detail ?? `Error updating file.`, {
                                variant: 'error'
                              });
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          color="primary"
                        >
                          {original.isActive ? <CheckBoxRounded /> : <CheckBoxOutlineBlank />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              }
            },

            {
              Header: 'Created Date',
              accessor: 'createdDate',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: IDynamicFileMangementTemplate } };
              }) => {
                const formattedDate = formatDate(original?.createdDate);
                return <div>{formattedDate}</div>;
              }
            },
            {
              Header: 'File Name',
              accessor: 'documentName',
              sort: false,
              Cell: ({
                cell: {
                  row: { original }
                }
              }: {
                cell: { row: { original: IDynamicFileMangementTemplate; index: number } };
              }) => {
                const displayName = original?.documentName;
                return <Typography>{displayName}</Typography>;
              }
            },
            {
              Header: 'Created By',
              accessor: 'createdBy',
              sort: false
            }
          ]}
          data={templateHistory ?? []}
          isLoading={isUpdating || isLoadingContractHistory}
          noResultsText="No file history found"
          hidePagination
        />
      </Box>
    </Modal>
  );
};
